import { useSuspenseQuery } from "@tanstack/react-query";
import {
  AccessorKeyColumnDefBase,
  createColumnHelper,
  IdIdentifier,
} from "@tanstack/react-table";
import {
  getControlName,
  getEndpointCategoryPartnerEventCodes,
  getPartnerEventName,
  getSCMCategory,
  getValidControlCategory,
  identityProviderPartnerEventCodes,
  identityProvidersNoControlPartnerEventCodes,
  isDefensiveControl,
  isDiscoveredDevicesControl,
  isEmailControl,
  isEndpointManagementControl,
  isIdentityProviderControl,
  isTruthy,
  isVulnerabilityManagementControl,
  useAccountControls,
} from "@theprelude/core";
import {
  ControlCategories,
  ControlCodes,
  InheritBehaviorFromCodeMappings,
  PartnerEndpoints,
  PartnerInboxes,
  PartnerUsers,
  SCMCategories,
  ValidControl,
  ValidControlCategory,
  ValidPartnerEventCode,
  ValidPartnerEvents,
} from "@theprelude/sdk";
import { useMemo } from "react";
import { prefixControlColumnLabel } from "@/components/filter-form/available-filters";
import {
  Column,
  cloudRegionColumn,
  controlFailureColumn,
  controlInstancesColumn,
  controlsColumn,
  crossControlsLastSeenColumn,
  daysSinceLastSeenColumn,
  defaultHidden,
  domainColumn,
  endpointCloudAccountIdColumn,
  endpointCloudInstanceIdColumn,
  endpointDeviceTypeColumn,
  endpointHostnameColumn,
  endpointMacAddressColumn,
  endpointPrimaryUserColumn,
  endpointSerialColumn,
  exceptedColumn,
  getColumnWith,
  getColumnWithControl,
  groupsColumn,
  hasAgentColumn,
  inboxEmailColumn,
  inboxTypesColumn,
  lastScanColumn,
  operationalStateColumn,
  partnerEndpointPlatformColumn,
  policyEndpointOwnerColumn,
  policyLastSeenColumn,
  policyVersionColumn,
  principalNamesColumn,
  risksColumn,
  statusesColumn,
  userDevicesColumn,
  userEmailColumn,
  userTypesColumn,
} from "@/components/table/columns";

export type SCMEndpointEntry = Record<string, unknown> & {
  endpoint_id: string;
  hostname: string | null;
  serial_number: string | null;
  cloud_account_id: string | null;
  cloud_instance_id: string | null;
  cloud_region: string | null;
  mac_addresses: string[] | null;
  primary_user_emails: string[] | null;
  platforms: string[] | null;
  device_types: string[] | null;
  last_seen: string | null;
};

let canvas: HTMLCanvasElement | undefined;
function getCanvasSafely() {
  try {
    canvas ??= document.createElement("canvas");
    return canvas;
  } catch {
    return undefined;
  }
}

const getLabelWidth = (() => {
  const canvasEl = getCanvasSafely();

  return (label?: string, defaultWidth = 250): number => {
    try {
      if (!label) return defaultWidth;
      const ctx = canvasEl?.getContext("2d");
      if (!ctx) return defaultWidth;
      ctx.font = "16px 'IBM Plex Sans', sans-serif";
      const textWidth = ctx.measureText(label).width;
      return textWidth + 60;
    } catch {
      return defaultWidth;
    }
  };
})();

const prependColumnMeta = <TData, TValue>({
  control,
  event,
  group,
  col,
}: {
  control?: ValidControl;
  event?: ValidPartnerEventCode;
  group?: string;
  col: Column<TData, TValue>;
}): Column<TData, TValue> => {
  const baseLabel = event
    ? `Days in ${getPartnerEventName(event, control?.id)}`
    : col.meta?.label;
  const label = prefixControlColumnLabel(baseLabel ?? "", control);
  const labelWidth = getLabelWidth(label);
  return {
    ...col,
    size: labelWidth,
    meta: { ...col.meta, label, group },
  };
};

export function getExceptionColumn(
  controlCategories: ValidControlCategory[] = [],
) {
  if (controlCategories.length === 1) {
    return getColumnWith(exceptedColumn, { category: controlCategories[0] });
  }
  return getColumnWith(exceptedColumn, {});
}

export function formatSCMEndpointData({
  endpoints,
  endpointControls,
  controlCategories = [],
}: {
  endpoints: PartnerEndpoints[];
  endpointControls: ValidControl[];
  controlCategories: ValidControlCategory[];
}): SCMEndpointEntry[] {
  return endpoints.map((endpoint): SCMEndpointEntry => {
    const controlSources = getSources(endpointControls, endpoint);

    const controlColumns = endpoint.instances
      .filter((e) => e.control !== ControlCodes.INTUNE_HOST_FIREWALL)
      .reduce(
        (acc, endpointControl) => ({
          ...acc,
          [controlsColumn.id]: controlSources,
          [controlInstancesColumn.id]: controlSources,
          [getColumnWithControl(
            policyLastSeenColumn,
            endpointControl.control,
            endpointControl.instance_id,
          ).id]: endpointControl?.last_seen,
          [getColumnWithControl(
            daysSinceLastSeenColumn,
            endpointControl.control,
            endpointControl.instance_id,
          ).id]: endpointControl?.last_seen,
          [getColumnWithControl(
            lastScanColumn,
            endpointControl.control,
            endpointControl.instance_id,
          ).id]: endpointControl?.last_scan,
          [getColumnWithControl(
            hasAgentColumn,
            endpointControl.control,
            endpointControl.instance_id,
          ).id]: endpointControl?.has_agent,
          [getColumnWithControl(
            operationalStateColumn,
            endpointControl.control,
            endpointControl.instance_id,
          ).id]: endpointControl?.operational_state,
          [getColumnWithControl(
            policyVersionColumn,
            endpointControl.control,
            endpointControl.instance_id,
          ).id]: endpointControl?.version,
          [getColumnWithControl(
            policyEndpointOwnerColumn,
            endpointControl.control,
            endpointControl.instance_id,
          ).id]: endpointControl?.device_ownership,
        }),
        {} as Record<string, unknown>,
      );
    const failureColumns = (endpoint.failures ?? []).reduce(
      (acc, endpointControl) => ({
        ...acc,
        [getColumnWith(
          controlFailureColumn,
          endpointControl.control && endpointControl.instance_id
            ? {
                instance_id: endpointControl.instance_id,
                control: endpointControl.control,
                event: endpointControl.event,
              }
            : {
                event: endpointControl.event,
              },
        ).id]: endpointControl?.[controlFailureColumn.id],
      }),
      {} as Record<string, unknown>,
    );
    const relevantExceptedColumn = getExceptionColumn(controlCategories);

    const excepted = {
      [relevantExceptedColumn.id]:
        controlCategories?.length === 1
          ? (endpoint.excepted.filter(
              (e) => e.category === controlCategories[0],
            ) ?? [])
          : (endpoint.excepted ?? []),
    };

    const relevantGroupsColumn = getColumnWith(groupsColumn, {});

    const groups = {
      [relevantGroupsColumn.id]: (endpoint.groups ?? []).map((g) => g.name),
    };

    return {
      endpoint_id: endpoint.endpoint_id,
      hostname: endpoint.hostname,
      serial_number: endpoint.serial_number,
      cloud_region: endpoint.cloud_region,
      cloud_account_id: endpoint.cloud_account_id,
      cloud_instance_id: endpoint.cloud_instance_id,
      mac_addresses: endpoint.mac_addresses,
      platforms: endpoint.platforms,
      device_types: endpoint.device_types,
      primary_user_emails: endpoint.primary_user_emails,
      last_seen: endpoint.last_seen,
      ...excepted,
      ...groups,
      ...controlColumns,
      ...failureColumns,
    };
  });
}

const EMPTY_ARRAY: never[] = [];

export function useEndpointControls(): ValidControl[] {
  const { data: controls = EMPTY_ARRAY } = useAccountControls();

  const endpointControls = useMemo(() => {
    const result = controls.filter(
      (c): c is ValidControl => getSCMCategory(c.id) === SCMCategories.ENDPOINT,
    );

    // The account will never have a `controls` entry for controls that behave like other
    // controls on the back-end. (e.g.: `DEFENDER_DISCOVERY` maps to `DEFENDER`, but will
    // never appear in `controls`.) To handle this, we can simply add an endpoint control
    // to the array here for any behavior-mapped control, duplicating the control data.
    for (let i = result.length - 1; i >= 0; i -= 1) {
      const endpointControl = result[i];
      const behavesAsControl =
        endpointControl && InheritBehaviorFromCodeMappings[endpointControl.id];

      if (endpointControl && behavesAsControl !== undefined) {
        result.push({ ...endpointControl, id: behavesAsControl });
      }
    }

    return result;
  }, [controls]);

  const filteredControls = useMemo(() => {
    return endpointControls.filter(
      (c): c is ValidControl => c.id !== ControlCodes.INTUNE_HOST_FIREWALL,
    );
  }, [endpointControls]);

  return filteredControls;
}

export function useIdentityControls(): ValidControl[] {
  const { data: controls = EMPTY_ARRAY } = useAccountControls();

  return useMemo(
    () =>
      controls.filter((c): c is ValidControl =>
        isIdentityProviderControl(c.id),
      ),
    [controls],
  );
}

export function useEmailControls(): ValidControl[] {
  const { data: controls = EMPTY_ARRAY } = useAccountControls();

  return useMemo(
    () => controls.filter((c): c is ValidControl => isEmailControl(c.id)),
    [controls],
  );
}

function getSources(
  integrations: ValidControl[],
  summary: PartnerEndpoints | PartnerUsers | PartnerInboxes,
): ValidControl[] {
  return integrations.filter(
    (c) =>
      summary.controls.includes(c.id) &&
      summary.instance_ids.includes(c.instance_id),
  );
}

export function useSCMEndpointColumns({
  endpointControls,
  noControlEventCodes,
  controlCategories,
}: {
  endpointControls: ValidControl[];
  noControlEventCodes: ValidPartnerEventCode[];
  controlCategories: ValidControlCategory[];
}): (AccessorKeyColumnDefBase<SCMEndpointEntry, any> &
  Partial<IdIdentifier<SCMEndpointEntry, any>>)[] {
  const { data: accountControls } = useAccountControls();

  const nameByControlInstance = Object.fromEntries(
    (accountControls ?? []).map((c) => [c.instance_id, c.name]),
  );

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<SCMEndpointEntry>();
    const relevantExceptedColumnDef = getExceptionColumn(controlCategories);
    const hostNameDef = getColumnWith(endpointHostnameColumn, {});
    const serialNumberDef = getColumnWith(endpointSerialColumn, {});
    const cloudAccountIdDef = getColumnWith(endpointCloudAccountIdColumn, {});
    const cloudInstanceIdDef = getColumnWith(endpointCloudInstanceIdColumn, {});
    const macAddressDef = getColumnWith(endpointMacAddressColumn, {});
    const deviceTypeDef = getColumnWith(endpointDeviceTypeColumn, {});
    const groupsDef = getColumnWith(groupsColumn, {});
    const platformColumnDef = getColumnWith(partnerEndpointPlatformColumn, {});
    const cloudRegionColumnDef = getColumnWith(cloudRegionColumn, {});
    const primaryUserColumnDef = getColumnWith(endpointPrimaryUserColumn, {});

    const relevantExceptedColumn = defaultHidden(
      relevantExceptedColumnDef,
      false,
    );
    const hostName = defaultHidden(hostNameDef, false);
    const serialNumber = defaultHidden(serialNumberDef, true);
    const cloudAccountId = defaultHidden(cloudAccountIdDef, true);
    const cloudInstanceId = defaultHidden(cloudInstanceIdDef, true);
    const macAddress = defaultHidden(macAddressDef, true);
    const groups = defaultHidden(groupsDef, true);
    const deviceType = defaultHidden(deviceTypeDef, true);
    const cloudRegion = defaultHidden(cloudRegionColumnDef, true);
    const primaryUser = defaultHidden(primaryUserColumnDef, false);
    const platform = defaultHidden(platformColumnDef, false);
    const baseColumns = [
      columnHelper.accessor(hostName.id, hostName),
      columnHelper.accessor(serialNumber.id, serialNumber),
      columnHelper.accessor(
        crossControlsLastSeenColumn.id,
        crossControlsLastSeenColumn,
      ),
      columnHelper.accessor(
        crossControlsLastSeenColumn.id,
        defaultHidden(
          {
            ...daysSinceLastSeenColumn,
            meta: {
              ...daysSinceLastSeenColumn.meta,
              label: "Days since Last Seen - Cross Controls",
            },
          },
          true,
        ),
      ),
      columnHelper.accessor(cloudAccountId.id, cloudAccountId),
      columnHelper.accessor(cloudInstanceId.id, cloudInstanceId),
      columnHelper.accessor(macAddress.id, macAddress),
      columnHelper.accessor(deviceType.id, deviceType),
      columnHelper.accessor(groups.id, groups),
      columnHelper.accessor(
        controlsColumn.id,
        defaultHidden(controlsColumn, false),
      ),
      columnHelper.accessor(
        controlInstancesColumn.id,
        defaultHidden(controlInstancesColumn, true),
      ),
      columnHelper.accessor(cloudRegion.id, cloudRegion),
      columnHelper.accessor(primaryUser.id, primaryUser),
      columnHelper.accessor(platform.id, platform),
    ];

    const getControlColumns = (c: ValidControl) => {
      const instanceName = nameByControlInstance[c.instance_id] ?? undefined;
      const group = `${getControlName(c.id)}${instanceName ? ` (${instanceName})` : ""}`;

      const lastSeenColumn = getColumnWithControl(
        policyLastSeenColumn,
        c.id,
        c.instance_id,
      );
      const dayslastSeenColumnDef = getColumnWithControl(
        daysSinceLastSeenColumn,
        c.id,
        c.instance_id,
      );
      const operationalStateColumnDef = getColumnWithControl(
        operationalStateColumn,
        c.id,
        c.instance_id,
      );
      const ownershipColumnDef = getColumnWithControl(
        policyEndpointOwnerColumn,
        c.id,
        c.instance_id,
      );
      const versionColumnDef = getColumnWithControl(
        policyVersionColumn,
        c.id,
        c.instance_id,
      );
      const lastScanDef = getColumnWithControl(
        lastScanColumn,
        c.id,
        c.instance_id,
      );
      const hasAgentDef = getColumnWithControl(
        hasAgentColumn,
        c.id,
        c.instance_id,
      );

      const dayslastSeenColumn = defaultHidden(dayslastSeenColumnDef, true);
      const operationalState = defaultHidden(operationalStateColumnDef, true);
      const ownershipColumn = defaultHidden(ownershipColumnDef, true);
      const versionColumn = defaultHidden(versionColumnDef, true);
      const lastScan = defaultHidden(lastScanDef, true);
      const hasAgent = defaultHidden(hasAgentDef, true);

      return [
        columnHelper.accessor(
          lastSeenColumn.id,
          prependColumnMeta({ control: c, col: lastSeenColumn, group }),
        ),
        columnHelper.accessor(
          dayslastSeenColumn.id,
          prependColumnMeta({ control: c, col: dayslastSeenColumn, group }),
        ),
        isVulnerabilityManagementControl(c.id) &&
          columnHelper.accessor(
            lastScan.id,
            prependColumnMeta({ control: c, col: lastScan, group }),
          ),
        c.id === ControlCodes.SENTINELONE &&
          columnHelper.accessor(
            operationalState.id,
            prependColumnMeta({
              control: c,
              col: operationalState,
              group,
            }),
          ),
        (isDefensiveControl(c.id) || isDiscoveredDevicesControl(c.id)) &&
          columnHelper.accessor(
            versionColumn.id,
            prependColumnMeta({ control: c, col: versionColumn, group }),
          ),
        isEndpointManagementControl(c.id) &&
          columnHelper.accessor(
            ownershipColumn.id,
            prependColumnMeta({ control: c, col: ownershipColumn, group }),
          ),
        isVulnerabilityManagementControl(c.id) &&
          columnHelper.accessor(
            hasAgent.id,
            prependColumnMeta({ control: c, col: hasAgent, group }),
          ),
      ].filter(isTruthy);
    };

    const getPartnerEventColumns = (c: ValidControl) => {
      const instanceName = nameByControlInstance[c.instance_id] ?? undefined;
      const group = `${getControlName(c.id)}${instanceName ? ` (${instanceName})` : ""}`;
      const category = getValidControlCategory(c.id);
      return getEndpointCategoryPartnerEventCodes(category).map((event) => {
        const eventColumnDef = getColumnWith(controlFailureColumn, {
          control: c.id,
          instance_id: c.instance_id,
          event,
        });
        const eventColumn = defaultHidden(eventColumnDef, true);

        return columnHelper.accessor(
          eventColumn.id,
          prependColumnMeta({ control: c, event, col: eventColumn, group }),
        );
      });
    };

    const noControlColumns = noControlEventCodes.map((event) => {
      const eventColumnDef = getColumnWith(controlFailureColumn, { event });
      const eventColumn = defaultHidden(eventColumnDef, true);

      return columnHelper.accessor(
        eventColumn.id,
        prependColumnMeta({
          control: undefined,
          event,
          col: eventColumn,
          group: "Control Failures",
        }),
      );
    });

    const controlColumns = endpointControls.flatMap((control) =>
      getControlColumns(control),
    );

    const defensiveEventColumns = endpointControls
      .filter((c) => isDefensiveControl(c.id))
      .flatMap((c) => getPartnerEventColumns(c));

    const vulnerabilityEventColumns = endpointControls
      .filter((c) => isVulnerabilityManagementControl(c.id))
      .flatMap((c) => getPartnerEventColumns(c));

    return [
      ...baseColumns,
      ...controlColumns,
      ...defensiveEventColumns,
      ...vulnerabilityEventColumns,
      ...noControlColumns,
      columnHelper.accessor(relevantExceptedColumn.id, relevantExceptedColumn),
    ];
  }, [
    controlCategories,
    endpointControls,
    noControlEventCodes,
    nameByControlInstance,
  ]);

  return columns;
}

export type SCMUserEntry = Record<string, unknown> & {
  userId: string;
  statuses: string[] | null;
  risks: string[] | null;
  userTypes: string[] | null;
  last_seen: string | null;
};

export function formatSCMUsersData({
  users,
  identityProviders,
}: {
  users: PartnerUsers[];
  identityProviders: ValidControl[];
}): SCMUserEntry[] {
  return users.map((user): SCMUserEntry => {
    const controlSources = getSources(identityProviders, user);

    const controlColumns = user.instances.reduce(
      (acc, userControl) => ({
        ...acc,
        [controlsColumn.id]: controlSources,
        [controlInstancesColumn.id]: controlSources,
        [getColumnWithControl(
          policyLastSeenColumn,
          userControl.control,
          userControl.instance_id,
        ).id]: userControl?.last_seen,
        [getColumnWithControl(
          daysSinceLastSeenColumn,
          userControl.control,
          userControl.instance_id,
        ).id]: userControl?.last_seen,
      }),
      {} as Record<string, unknown>,
    );
    const failureColumns = (user.failures ?? []).reduce(
      (acc, userControl) => ({
        ...acc,
        [getColumnWith(
          controlFailureColumn,
          userControl.control
            ? {
                control: userControl.control,
                instance_id: userControl.instance_id ?? undefined,
                event: userControl.event,
              }
            : {
                event: userControl.event,
              },
        ).id]: userControl?.[controlFailureColumn.id],
      }),
      {} as Record<string, unknown>,
    );

    const excepted = {
      [getColumnWith(exceptedColumn, { category: ControlCategories.IDENTITY })
        .id]: user.excepted ?? [],
    };

    const relevantGroupsColumn = getColumnWith(groupsColumn, {});

    const groups = {
      [relevantGroupsColumn.id]: (user.groups ?? []).map((g) => g.name),
    };

    return {
      email: user.email,
      userId: user.user_id,
      devices: user.devices,
      statuses: user.statuses,
      risks: user.risks,
      userTypes: user.user_types,
      ...excepted,
      ...groups,
      ...controlColumns,
      ...failureColumns,
      last_seen: user.last_seen,
    };
  });
}

export function useSCMUsersColumns({
  instanceId,
  identityProviders,
}: {
  instanceId?: string;
  identityProviders: ValidControl[];
}): (AccessorKeyColumnDefBase<SCMUserEntry, any> &
  Partial<IdIdentifier<SCMUserEntry, any>>)[] {
  const { data: accountControls } = useAccountControls();
  const nameByControlInstance = Object.fromEntries(
    (accountControls ?? []).map((c) => [c.instance_id, c.name]),
  );

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<SCMUserEntry>();
    const relevantExceptedColumn = getExceptionColumn([
      ControlCategories.IDENTITY,
    ]);
    const groupsDef = getColumnWith(groupsColumn, {});
    const groups = defaultHidden(groupsDef);

    const baseColumns = [
      columnHelper.accessor(userEmailColumn.id, userEmailColumn),
      columnHelper.accessor(
        userDevicesColumn.id,
        defaultHidden(userDevicesColumn),
      ),
      columnHelper.accessor(
        crossControlsLastSeenColumn.id,
        crossControlsLastSeenColumn,
      ),
      columnHelper.accessor(
        crossControlsLastSeenColumn.id,
        defaultHidden(
          {
            ...daysSinceLastSeenColumn,
            meta: {
              ...daysSinceLastSeenColumn.meta,
              label: "Days since Last Seen - Cross Controls",
            },
          },
          true,
        ),
      ),
      columnHelper.accessor(groups.id, defaultHidden(groups)),
      columnHelper.accessor(controlsColumn.id, defaultHidden(controlsColumn)),
      columnHelper.accessor(
        controlInstancesColumn.id,
        defaultHidden(controlInstancesColumn),
      ),
      columnHelper.accessor(statusesColumn.id, statusesColumn),
      columnHelper.accessor(risksColumn.id, defaultHidden(risksColumn)),
      columnHelper.accessor(userTypesColumn.id, userTypesColumn),
    ];

    const getControlColumns = (c: ValidControl) => {
      const instanceName = nameByControlInstance[c.instance_id] ?? undefined;
      const group = `${getControlName(c.id)}${instanceName ? ` (${instanceName})` : ""}`;
      const hideColumn = !!instanceId && c.instance_id !== instanceId;

      const lastSeen = defaultHidden(
        getColumnWithControl(policyLastSeenColumn, c.id, c.instance_id),
        hideColumn,
      );
      const dayslastSeenColumnDef = getColumnWithControl(
        daysSinceLastSeenColumn,
        c.id,
        c.instance_id,
      );
      const dayslastSeenColumn = defaultHidden(dayslastSeenColumnDef, true);

      return [
        columnHelper.accessor(
          lastSeen.id,
          prependColumnMeta({ control: c, col: lastSeen, group }),
        ),
        columnHelper.accessor(
          dayslastSeenColumn.id,
          prependColumnMeta({ control: c, col: dayslastSeenColumn, group }),
        ),
      ].filter(isTruthy);
    };

    const getPartnerEventColumns = (c: ValidControl) => {
      const instanceName = nameByControlInstance[c.instance_id] ?? undefined;
      const group = `${getControlName(c.id)}${instanceName ? ` (${instanceName})` : ""}`;

      return identityProviderPartnerEventCodes.map((event) => {
        const eventColumn = defaultHidden(
          getColumnWith(controlFailureColumn, {
            control: c.id,
            instance_id: c.instance_id,
            event,
          }),
        );
        return columnHelper.accessor(
          eventColumn.id,
          prependColumnMeta({ control: c, event, col: eventColumn, group }),
        );
      });
    };

    const controlColumns = identityProviders.flatMap((control) =>
      getControlColumns(control),
    );

    const eventColumns = identityProviders.flatMap((c) =>
      getPartnerEventColumns(c),
    );

    const noControlColumns = identityProvidersNoControlPartnerEventCodes.map(
      (event) => {
        const eventColumnDef = getColumnWith(controlFailureColumn, { event });
        const eventColumn = defaultHidden(eventColumnDef);

        return columnHelper.accessor(
          eventColumn.id,
          prependColumnMeta({
            control: undefined,
            event,
            col: eventColumn,
            group: "Control Failures",
          }),
        );
      },
    );

    return [
      ...baseColumns,
      ...controlColumns,
      ...eventColumns,
      ...noControlColumns,
      columnHelper.accessor(
        relevantExceptedColumn.id,
        defaultHidden(relevantExceptedColumn),
      ),
    ];
  }, [identityProviders, instanceId, nameByControlInstance]);

  return columns;
}

export type SCMInboxEntry = Record<string, unknown> & {
  email: string;
  domain: string | null;
  principalNames: string[] | null;
  inboxTypes: string[] | null;
};

export function formatSCMInboxesData({
  inboxes,
  emailProviders,
}: {
  inboxes: PartnerInboxes[];
  emailProviders: ValidControl[];
}): SCMInboxEntry[] {
  return inboxes.map((inbox): SCMInboxEntry => {
    const controlSources = getSources(emailProviders, inbox);

    const controlColumns = inbox.instances.reduce(
      (acc, inboxControl) => ({
        ...acc,
        [controlsColumn.id]: controlSources,
        [controlInstancesColumn.id]: controlSources,
      }),
      {} as Record<string, unknown>,
    );

    const excepted = {
      [getColumnWith(exceptedColumn, { category: ControlCategories.EMAIL }).id]:
        inbox.excepted ?? [],
    };

    const relevantGroupsColumn = getColumnWith(groupsColumn, {});

    const groups = {
      [relevantGroupsColumn.id]: (inbox.groups ?? []).map((g) => g.name),
    };

    return {
      email: inbox.email,
      domain: inbox.domain,
      principalNames: inbox.principal_names,
      inboxTypes: inbox.inbox_types,
      ...excepted,
      ...groups,
      ...controlColumns,
    };
  });
}

export function useSCMInboxesColumns(): (AccessorKeyColumnDefBase<
  SCMInboxEntry,
  any
> &
  Partial<IdIdentifier<SCMInboxEntry, any>>)[] {
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<SCMInboxEntry>();
    const relevantExceptedColumn = getExceptionColumn([
      ControlCategories.EMAIL,
    ]);
    const groupsDef = getColumnWith(groupsColumn, {});
    const groups = defaultHidden(groupsDef);

    const baseColumns = [
      columnHelper.accessor(inboxEmailColumn.id, inboxEmailColumn),
      columnHelper.accessor(groups.id, groups),
      columnHelper.accessor(controlsColumn.id, defaultHidden(controlsColumn)),
      columnHelper.accessor(
        controlInstancesColumn.id,
        defaultHidden(controlInstancesColumn),
      ),
      columnHelper.accessor(domainColumn.id, defaultHidden(domainColumn)),
      columnHelper.accessor(
        principalNamesColumn.id,
        defaultHidden(principalNamesColumn),
      ),
      columnHelper.accessor(inboxTypesColumn.id, inboxTypesColumn),
    ];

    return [
      ...baseColumns,
      columnHelper.accessor(
        relevantExceptedColumn.id,
        defaultHidden(relevantExceptedColumn),
      ),
    ];
  }, []);

  return columns;
}
